<template>
  <div class="security-settings-page">
    <b-card
      class="security-settings-shell"
    >
      <b-overlay
        :show="loading"
        rounded="sm"
      >
        <b-form @submit.prevent="savePolicy">
          <b-card
            class="security-settings-card border mb-0"
            no-body
          >
            <b-card-body>
              <div class="security-settings-card-header">
                <div class="security-settings-title">
                  <feather-icon
                    icon="ShieldIcon"
                    size="18"
                    class="mr-50 text-primary"
                  />
                  <h5 class="mb-0">
                    {{ $t('securitySettings.sessionPolicy') }}
                  </h5>
                </div>
                <div
                  v-if="updatedAt || updatedBy"
                  class="security-settings-updated"
                >
                  {{ $t('securitySettings.updatedBy') }} {{ updatedBy || '-' }}
                  <span class="mx-50">|</span>
                  {{ updatedAtText }}
                </div>
              </div>

              <div class="security-policy-grid">
                <div class="security-policy-item">
                  <div class="security-policy-item-header">
                    <feather-icon
                      icon="ClockIcon"
                      size="16"
                      class="text-primary"
                    />
                    <div>
                      <div class="security-policy-label">
                        {{ $t('securitySettings.idleTimeout') }}
                      </div>
                      <small class="security-policy-description">
                        {{ $t('securitySettings.idleTimeoutDescription') }}
                      </small>
                    </div>
                  </div>
                  <b-form-group
                    label-for="idleTimeoutMinutes"
                    class="mb-0"
                  >
                    <b-input-group size="sm">
                      <b-form-input
                        id="idleTimeoutMinutes"
                        v-model.number="form.idleTimeoutMinutes"
                        type="number"
                        :min="limits.idleTimeoutMinutes.min"
                        :max="limits.idleTimeoutMinutes.max"
                        :state="fieldState('idleTimeoutMinutes')"
                      />
                      <b-input-group-append is-text>
                        {{ $t('securitySettings.minutes') }}
                      </b-input-group-append>
                    </b-input-group>
                    <small class="security-policy-range">
                      {{ policyRangeText('idleTimeoutMinutes') }}
                    </small>
                    <small
                      v-if="validationErrors.idleTimeoutMinutes"
                      class="text-danger"
                    >
                      {{ validationErrors.idleTimeoutMinutes }}
                    </small>
                  </b-form-group>
                </div>

                <div class="security-policy-item">
                  <div class="security-policy-item-header">
                    <feather-icon
                      icon="BellIcon"
                      size="16"
                      class="text-warning"
                    />
                    <div>
                      <div class="security-policy-label">
                        {{ $t('securitySettings.idleWarning') }}
                      </div>
                      <small class="security-policy-description">
                        {{ $t('securitySettings.idleWarningDescription') }}
                      </small>
                    </div>
                  </div>
                  <b-form-group
                    label-for="idleWarningMinutes"
                    class="mb-0"
                  >
                    <b-input-group size="sm">
                      <b-form-input
                        id="idleWarningMinutes"
                        v-model.number="form.idleWarningMinutes"
                        type="number"
                        :min="limits.idleWarningMinutes.min"
                        :max="limits.idleWarningMinutes.max"
                        :state="fieldState('idleWarningMinutes')"
                      />
                      <b-input-group-append is-text>
                        {{ $t('securitySettings.minutes') }}
                      </b-input-group-append>
                    </b-input-group>
                    <small class="security-policy-range">
                      {{ policyRangeText('idleWarningMinutes') }}
                    </small>
                    <small
                      v-if="validationErrors.idleWarningMinutes"
                      class="text-danger"
                    >
                      {{ validationErrors.idleWarningMinutes }}
                    </small>
                  </b-form-group>
                </div>

                <div class="security-policy-item">
                  <div class="security-policy-item-header">
                    <feather-icon
                      icon="ActivityIcon"
                      size="16"
                      class="text-success"
                    />
                    <div>
                      <div class="security-policy-label">
                        {{ $t('securitySettings.onlineWindow') }}
                      </div>
                      <small class="security-policy-description">
                        {{ $t('securitySettings.onlineWindowDescription') }}
                      </small>
                    </div>
                  </div>
                  <b-form-group
                    label-for="onlineWindowMinutes"
                    class="mb-0"
                  >
                    <b-input-group size="sm">
                      <b-form-input
                        id="onlineWindowMinutes"
                        v-model.number="form.onlineWindowMinutes"
                        type="number"
                        :min="limits.onlineWindowMinutes.min"
                        :max="limits.onlineWindowMinutes.max"
                        :state="fieldState('onlineWindowMinutes')"
                      />
                      <b-input-group-append is-text>
                        {{ $t('securitySettings.minutes') }}
                      </b-input-group-append>
                    </b-input-group>
                    <small class="security-policy-range">
                      {{ policyRangeText('onlineWindowMinutes') }}
                    </small>
                    <small
                      v-if="validationErrors.onlineWindowMinutes"
                      class="text-danger"
                    >
                      {{ validationErrors.onlineWindowMinutes }}
                    </small>
                  </b-form-group>
                </div>
              </div>
            </b-card-body>
          </b-card>

          <div class="security-settings-actions">
            <b-button
              v-ripple.400="'rgba(110, 107, 123, 0.12)'"
              variant="outline-secondary"
              type="button"
              :disabled="saving || !hasDefaultChanges"
              @click="resetToDefaults"
            >
              <feather-icon
                icon="RotateCcwIcon"
                size="14"
                class="mr-50"
              />
              {{ $t('securitySettings.resetDefaults') }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              type="submit"
              :disabled="saving || !isValid || !hasChanges"
            >
              <feather-icon
                icon="SaveIcon"
                size="14"
                class="mr-50"
              />
              <span v-if="saving">{{ $t('Saving') }}</span>
              <span v-else>{{ $t('Save changes') }}</span>
            </b-button>
          </div>
        </b-form>
      </b-overlay>
    </b-card>
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BCardBody,
  BForm,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BOverlay,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import useJwt from '@/auth/jwt/useJwt'

const defaultLimits = {
  idleTimeoutMinutes: { min: 2, max: 240 },
  idleWarningMinutes: { min: 1, max: 60 },
  onlineWindowMinutes: { min: 1, max: 30 },
}

const defaultPolicyForm = {
  idleTimeoutMinutes: 30,
  idleWarningMinutes: 5,
  onlineWindowMinutes: 5,
}

export default {
  directives: {
    Ripple,
  },
  components: {
    BButton,
    BCard,
    BCardBody,
    BForm,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BOverlay,
  },
  data() {
    return {
      loading: false,
      saving: false,
      limits: { ...defaultLimits },
      defaults: { ...defaultPolicyForm },
      form: { ...defaultPolicyForm },
      savedForm: null,
      updatedAt: null,
      updatedBy: '',
    }
  },
  computed: {
    validationErrors() {
      const errors = {
        idleTimeoutMinutes: this.rangeError('idleTimeoutMinutes'),
        idleWarningMinutes: this.rangeError('idleWarningMinutes'),
        onlineWindowMinutes: this.rangeError('onlineWindowMinutes'),
      }

      if (!errors.idleWarningMinutes && Number(this.form.idleWarningMinutes) >= Number(this.form.idleTimeoutMinutes)) {
        errors.idleWarningMinutes = this.$t('securitySettings.warningLessThanTimeout')
      }

      return errors
    },
    isValid() {
      return Object.values(this.validationErrors).every(value => !value)
    },
    hasChanges() {
      if (!this.savedForm) return false
      return Object.keys(this.form).some(key => Number(this.form[key]) !== Number(this.savedForm[key]))
    },
    hasDefaultChanges() {
      return Object.keys(this.form).some(key => Number(this.form[key]) !== Number(this.defaults[key]))
    },
    updatedAtText() {
      if (!this.updatedAt) return '-'
      return new Intl.DateTimeFormat(this.getLocale(), {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(this.updatedAt))
    },
  },
  mounted() {
    this.loadPolicy()
  },
  methods: {
    async loadPolicy() {
      this.loading = true
      try {
        const { data } = await useJwt.sessionPolicy()
        this.applyPolicy(data)
      } catch (error) {
        this.showToast('danger', 'AlertCircleIcon', this.$t('securitySettings.loadError'))
      } finally {
        this.loading = false
      }
    },
    async savePolicy() {
      if (!this.isValid || !this.hasChanges) return

      const confirmed = await this.confirmSavePolicy()
      if (!confirmed) return

      this.saving = true
      try {
        const { data } = await useJwt.updateSessionPolicy({
          idleTimeoutMinutes: Number(this.form.idleTimeoutMinutes),
          idleWarningMinutes: Number(this.form.idleWarningMinutes),
          onlineWindowMinutes: Number(this.form.onlineWindowMinutes),
        })
        this.applyPolicy(data)
        this.showToast('success', 'CheckIcon', this.$t('securitySettings.saveSuccess'))
      } catch (error) {
        const message = error?.response?.data?.error || this.$t('securitySettings.saveError')
        this.showToast('danger', 'AlertCircleIcon', message)
      } finally {
        this.saving = false
      }
    },
    applyPolicy(data) {
      this.defaults = this.normalizePolicyForm(data?.defaults || defaultPolicyForm)
      this.limits = {
        idleTimeoutMinutes: data?.limits?.idleTimeoutMinutes || defaultLimits.idleTimeoutMinutes,
        idleWarningMinutes: data?.limits?.idleWarningMinutes || defaultLimits.idleWarningMinutes,
        onlineWindowMinutes: data?.limits?.onlineWindowMinutes || defaultLimits.onlineWindowMinutes,
      }
      this.form = this.normalizePolicyForm(data || defaultPolicyForm)
      this.savedForm = { ...this.form }
      this.updatedAt = data?.updatedAt || null
      this.updatedBy = data?.updatedBy || ''
    },
    normalizePolicyForm(source) {
      return {
        idleTimeoutMinutes: Number(source?.idleTimeoutMinutes || defaultPolicyForm.idleTimeoutMinutes),
        idleWarningMinutes: Number(source?.idleWarningMinutes || defaultPolicyForm.idleWarningMinutes),
        onlineWindowMinutes: Number(source?.onlineWindowMinutes || defaultPolicyForm.onlineWindowMinutes),
      }
    },
    resetToDefaults() {
      this.form = { ...this.defaults }
    },
    async confirmSavePolicy() {
      const result = await this.$swal({
        dark: true,
        title: this.$t('securitySettings.confirmSaveTitle'),
        text: this.$t('securitySettings.confirmSaveText'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('securitySettings.confirmSave'),
        cancelButtonText: this.$t('Cancel'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })
      return result.value
    },
    rangeError(field) {
      const value = Number(this.form[field])
      const limit = this.limits[field] || defaultLimits[field]
      if (!Number.isInteger(value) || value < limit.min || value > limit.max) {
        return this.$t('securitySettings.rangeError', {
          min: limit.min,
          max: limit.max,
        })
      }
      return ''
    },
    fieldState(field) {
      return this.validationErrors[field] ? false : null
    },
    policyRangeText(field) {
      const limit = this.limits[field] || defaultLimits[field]
      return this.$t('securitySettings.rangeHint', {
        min: limit.min,
        max: limit.max,
      })
    },
    showToast(variant, icon, text) {
      this.$toast({
        component: ToastificationContent,
        position: 'top-right',
        props: {
          title: variant === 'success' ? this.$t('Success') : this.$t('Error'),
          icon,
          variant,
          text,
        },
      })
    },
    getLocale() {
      return (this.$i18n && this.$i18n.locale) || navigator.language || 'en'
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-sweetalert.scss';

.security-settings-page {
  .security-settings-shell {
    max-width: 1024px;
  }

  .security-settings-shell > .card-body {
    padding-top: 1.35rem;
  }

  .security-settings-card {
    border-color: rgba(234, 84, 85, .18) !important;
    box-shadow: none;
  }

  .security-settings-card > .card-body {
    padding: 1.35rem;
  }

  .security-settings-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1.25rem;
  }

  .security-settings-title {
    display: inline-flex;
    align-items: center;
  }

  .security-settings-updated {
    color: #8a8492;
    font-size: .857rem;
    white-space: nowrap;
  }

  .security-policy-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1rem;
  }

  .security-policy-item {
    min-width: 0;
    padding: 1rem;
    border: 1px solid rgba(110, 107, 123, .12);
    border-radius: .358rem;
    background: #fff;
  }

  .security-policy-item-header {
    display: flex;
    align-items: flex-start;
    gap: .65rem;
    min-height: 2.65rem;
    margin-bottom: .85rem;
  }

  .security-policy-label {
    color: #5e5873;
    font-weight: 600;
    line-height: 1.2;
  }

  .security-policy-description {
    display: block;
    margin-top: .25rem;
    color: #8a8492;
    font-size: .785rem;
    line-height: 1.35;
  }

  .security-policy-range {
    display: block;
    margin-top: .45rem;
    color: #8a8492;
    font-size: .785rem;
    line-height: 1.2;
  }

  .security-policy-item .input-group-text {
    min-width: 3.75rem;
    justify-content: center;
    color: #6e6b7b;
    font-weight: 600;
  }

  .security-settings-actions {
    display: flex;
    justify-content: flex-end;
    gap: .75rem;
    margin-top: 1rem;
  }

  .security-settings-actions .btn {
    min-width: 11rem;
  }

  @media (max-width: 991.98px) {
    .security-policy-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 767.98px) {
    .security-settings-card-header {
      align-items: flex-start;
      flex-direction: column;
    }

    .security-settings-updated {
      white-space: normal;
    }

    .security-settings-actions {
      flex-direction: column;
    }

    .security-settings-actions .btn {
      width: 100%;
    }
  }
}

.dark-layout {
  .security-settings-page {
    .security-settings-card {
      border-color: rgba(234, 84, 85, .28) !important;
      background-color: var(--hdg-panel, #283046);
    }

    .security-policy-item {
      border-color: rgba(255, 255, 255, .08);
      background-color: var(--hdg-surface, #161d31);
      box-shadow: 0 .5rem 1.25rem rgba(0, 0, 0, .16);
    }

    .security-policy-label {
      color: var(--hdg-ink, #d0d2d6);
    }

    .security-policy-description,
    .security-policy-range,
    .security-settings-updated {
      color: var(--hdg-chart-fore, #b4b7bd);
    }

    .security-policy-item .form-control,
    .security-policy-item .input-group-text {
      border-color: var(--hdg-border, #3b4253);
      background-color: var(--hdg-panel, #283046);
      color: var(--hdg-body, #d0d2d6);
    }

    .security-policy-item .input-group-text {
      color: var(--hdg-chart-fore, #b4b7bd);
    }
  }
}
</style>
